import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Image,
  Td,
  Text,
  Tooltip,
  Tr,
  VStack,
  Heading,
  useDisclosure,
  SimpleGrid,
  useBoolean,
  Portal,
  useToast,
} from '@chakra-ui/react';

import { useRouter } from 'next/router';
import * as React from 'react';
import truncate from 'lodash/truncate';

import { useMemberManagement } from '@/hooks/useMemberManagement';
import { useMembershipDisplayConfig } from '@/hooks/useMembershipDisplayConfig';
import { MostRelevantMembershipTermAlertTag } from '@/components/MostRelevantMembershipTermAlertTag';
import {
  getDisplayMembershipStatus,
  MEMBERSHIP_STATUS_COLOR,
  READABLE_MEMBERSHIP_STATUS,
} from '@/utilities';
import { OrganizationResult } from '../../../hooks/useOrganizationsPagination/useOrganizationsPagination';
import { OrganiizationIcon } from '@/imports/ui/chakra/customIcon/oorganization';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  EditIcon,
  EyeIcon,
  MapPinIcon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
  PhoneIcon,
} from '@/imports/ui/chakra/feather';
import { AccordionMembershipCard } from '@/components/AccordionMembershipCard';
import { dinero } from 'dinero.js';

import { gql, useMutation, useQuery } from '@apollo/client';
import { USD } from '@dinero.js/currencies';
import { Card } from '@/imports/ui/chakra/components/Card';
import { BannerImageMenu } from '@/imports/ui/components/BannerImageMenu';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { ProfileImageMenu } from '@/imports/ui/components/ProfileImageMenu';
import { CompanyInfoEdit } from '@/components/admin/sponsorships/Sponsors/components/Company/components/CompanyInfoEdit';
import { CompanyInfoEdit_OrganizationFragment } from '@/components/admin/sponsorships/Sponsors/components/Company/components/CompanyInfoEdit/graphql-types/CompanyInfoEdit_OrganizationFragment';
import { ATTRIBUTE_FRAGMENT } from '@/modules/attributes';
import { useImageUpload } from '@/hooks/useImageUpload';
import { EPrepImagesSource } from '@/graphql-types/globalTypes';
import { CompanyDetailsRow_UpdateOrganization, CompanyDetailsRow_UpdateOrganizationVariables } from './graphql-types/CompanyDetailsRow_UpdateOrganization';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { ISelectionState } from '@/imports/ui/useSelectionState';
import { PhoneNoIcon } from '@/imports/ui/chakra/customIcon/PhoneNoIcon';
import { EmailIcon } from '@/imports/ui/chakra/customIcon/EmailIcon';
import { GlobeIcon } from '@/imports/ui/chakra/customIcon/GlobeIcon';
import { MembershipEmptyCard } from '@/components/MembershipEmptyCard';
import { getOrganizationAdminBasePath } from '@/utilities/archivedOrganizationPaths';

export const organizationListFragment = gql`
  fragment CompanyDetailsRow_OrganizationFragment on Organization {
    _id
    name
    displayName
    hidden
    isSponsor
    isMember
    currentUserOrganizationPermission {
      canManageOrganizationProfile
    }
    profileAttributes(input: {}) {
      results {
        attribute {
          _id
          ...AttributeFragment
        }
      }
    }
    membershipData {
      mostRelevantMembershipTermAlert
      membershipStatus
      currentMembershipTerm {
        _id
        membershipType {
          _id
          name
        }
      }
    }
    accountOwners {
      _id
      email
    }
    ...OrganizationProfileImage_OrganizationFragment
  }
  ${OrganizationProfileImage.organizationFragment}
  ${ATTRIBUTE_FRAGMENT}
`;

/** Loaded when a row is expanded — membership detail and banner image. */
export const ORGANIZATION_DETAIL_QUERY = gql`
  query OrganizationRow_Organization_Query(
    $input: SingleOrganizationInput!
    $relevantMembershipTermsInput: RelevantMembershipTermsInput
  ) {
    organization(input: $input) {
      result {
        _id
        displayName
        creditBalance
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        membershipData {
          ...AccordionMembershipCard_MembershipDataFragment
        }
        accountOwners {
          _id
        }
      }
    }
  }
  ${AccordionMembershipCard.membershipDataFragment}
`;

const UPDATE_ORGANIZATION_IMAGE = gql`
  mutation CompanyDetailsRow_UpdateOrganization(
    $input: UpdateOrganizationV2Input!
  ) {
    updateOrganizationV2(input: $input) {
      ... on UpdateOrganizationV2Success {
        organization {
          _id
          ...CompanyDetailsRow_OrganizationFragment
          bannerImage {
            _id
            imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
          }
        }
      }
    }
  }
  ${organizationListFragment}
`;

type OrganizationRowProps = {
  organization: OrganizationResult;
  selection: ISelectionState<OrganizationResult>;
  toggleOne: (organization: OrganizationResult) => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  colSpan?: number;
  archivedOnly?: boolean;
};

export const OrganizationRow: React.FC<OrganizationRowProps> = ({
  organization,
  selection,
  toggleOne,
  isExpanded: controlledIsExpanded,
  onToggleExpand,
  colSpan = 8,
  archivedOnly = false,
}) => {
  const router = useRouter();
  const toast = useToast();
  const memberManagement = useMemberManagement();
  // When on, remap Ex-Member → Non-Member in Membership Status column
  const { showActiveAndNonMembersOnly, loading: displayConfigLoading } =
    useMembershipDisplayConfig();
  const [localIsExpanded, setLocalIsExpanded] = React.useState(false);

  // Switch on: Ex-Member → Non-Member; switch off: keep original status
  // Skip until config is ready so we never briefly show wrong badges
  const displayMembershipStatus =
    !displayConfigLoading &&
    organization.membershipData?.membershipStatus
      ? getDisplayMembershipStatus(
          organization.membershipData.membershipStatus,
          showActiveAndNonMembersOnly
        )
      : undefined;

  const isExpanded =
    controlledIsExpanded !== undefined ? controlledIsExpanded : localIsExpanded;

  const detailQuery = useQuery(ORGANIZATION_DETAIL_QUERY, {
    variables: {
      input: { id: organization._id },
      relevantMembershipTermsInput: { includeRecentlyLapsed: true },
    },
    skip: !isExpanded,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const detailResult = detailQuery.data?.organization?.result;
  const membershipData = detailResult?.membershipData;
  const bannerImage = detailResult?.bannerImage;
  const isLoadingDetail = detailQuery.loading && !detailResult;

  const displayName = truncate(organization.displayName, { length: 35 });
  const membershipTypeName = truncate(
    organization.membershipData?.currentMembershipTerm?.membershipType?.name ?? 'None',
    { length: 30 }
  );
  const membershipStatus =
    organization.membershipData?.membershipStatus as keyof typeof MEMBERSHIP_STATUS_COLOR;

  const orgDetailsBasePath = getOrganizationAdminBasePath({
    organizationId: organization._id,
    archivedOnly,
  });

  const handleRowClick = () => {
    router.push(`${orgDetailsBasePath}/details`);
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onToggleExpand) {
      onToggleExpand();
      return;
    }

    setLocalIsExpanded((prev) => !prev);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    toggleOne(organization);
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    router.push(`${orgDetailsBasePath}/details`);
  };

  const [updateOrganizationImage] = useMutation<
    CompanyDetailsRow_UpdateOrganization,
    CompanyDetailsRow_UpdateOrganizationVariables
  >(UPDATE_ORGANIZATION_IMAGE);

  const { uploadImages: uploadBannerImages } = useImageUpload(
    EPrepImagesSource.SPONSOR_BANNER_IMAGE
  );
  const { uploadImages: uploadProfilePicture } = useImageUpload(
    EPrepImagesSource.SPONSOR_PROFILE_PICTURE
  );

  const result = organization;
  const { currency } = useDefaultCurrency();
  const detailOrganization = organization;
  const editModal = useDisclosure();
  const [isUpdatingImage, setIsUpdatingImage] = useBoolean();
  const [isRestoring, setIsRestoring] = React.useState(false);

  const handleRestoreOrganization = async (): Promise<void> => {
    setIsRestoring(true);
    try {
      await updateOrganizationImage({
        variables: {
          input: {
            id: organization._id,
            data: { hidden: false },
          },
        },
        refetchQueries: [
          'useOrganizationsPagination_OrganizationSearch',
          'Sponsors_Organizations',
        ],
        awaitRefetchQueries: true,
      });
      toast({
        title: "You've successfully restored this organization.",
        status: 'success',
      });
    } catch (err) {
      toast({
        title: 'Failed to restore organization',
        status: 'error',
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const getUpdate = (
    type: 'banner' | 'profile'
  ): ((file: any) => Promise<any>) => {
  return async (file: any): Promise<any> => {
    const isBannerImage = type === 'banner';
    setIsUpdatingImage.on();
    if (file === null) {
      await updateOrganizationImage({
        variables: {
          input: {
            id: organization._id,
            data: isBannerImage
              ? { sponsorBannerImageId: null }
              : { sponsorProfilePictureId: null },
          },
        },
      });
      setIsUpdatingImage.off();
      return;
    }
    const resource = isBannerImage
      ? await uploadBannerImages([file])
      : await uploadProfilePicture([file]);
    if (resource) {
      await updateOrganizationImage({
        variables: {
          input: {
            id: organization._id,
            data: isBannerImage
              ? { sponsorBannerImageId: resource[0] }
              : { sponsorProfilePictureId: resource[0] },
          },
        },
      });
    }
    setIsUpdatingImage.off();
  };
};

const attributes = React.useMemo(() => {
    const results = result?.profileAttributes?.results ?? [];

    const getValue = (key: string) => {
      const found = results.find(({ attribute }: any) => {
        const label = attribute?.label?.toLowerCase?.() ?? '';
        const slug = attribute?.slug?.toLowerCase?.() ?? '';
        return label === key || slug === key;
      });

      const attribute = found?.attribute;
      if (!attribute) return '-';

     return (() => {
      switch (attribute.__typename) {
        case 'FreeTextAttribute':
          return attribute.freeTextValue ?? '—';

        default:
          return '—';
      }
    })();
    };

    return {
      email: getValue('email'),
      phone: getValue('primary phone'),
      website: getValue('website'),
      location: [getValue('city'), getValue('state')]
        .filter(Boolean)
        .join(', '),
    };
  }, [result?.profileAttributes?.results]);

  const summaryEmail = attributes.email || organization.accountOwners?.[0]?.email || '-';

  return (
    <>
      <Tr
        _hover={{ bg: 'bgPrimary.500'}}
      >
        <Td  width={10} pr="2" pl="2">
          <HStack spacing="1">
            <IconButton
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
              icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              size="md"
              variant="ghost"
              p={0}
              minW="auto"
              minH="auto"
              h="auto"
              w="auto"
              onClick={handleExpandToggle}
            />
            <Checkbox
              isChecked={
                (selection.allSelected &&
                  !selection.excludedItems
                    .map((org) => org._id)
                    .includes(organization._id)) ||
                (!selection.allSelected &&
                  selection.includedItems
                    .map((org) => org._id)
                    .includes(organization._id))
              }
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
            />
          </HStack>
        </Td>

        <Td pr="2" pl="2"  _hover={{cursor: 'pointer' }}
        onClick={handleRowClick}>
          <Stack direction="row" spacing="4" alignItems="center">
            <Avatar
              size="md"
              name={displayName}
              src={organization.profilePicture?.imageProxy || undefined}
            />

            <Stack spacing="0">
              <Tooltip label={organization.displayName}>
                <Text textStyle="compactStrong" noOfLines={1}>
                  <span>{displayName}</span>{' '}
                  <OrganiizationIcon color="primary.500" boxSize="20px" />
                </Text>
              </Tooltip>

              <Text textStyle="compact">
                {summaryEmail}
              </Text>
            </Stack>
          </Stack>
        </Td>

        <Td pr="2" pl="2">
          <Text textStyle="compact">
            {attributes.phone || "-"}
          </Text>
        </Td>

        {memberManagement.isActive ? (
          <Td pr="2" pl="2">
            <HStack>
              {displayMembershipStatus && (
                <Badge
                  color={MEMBERSHIP_STATUS_COLOR[displayMembershipStatus]}
                  bg={MEMBERSHIP_STATUS_COLOR[displayMembershipStatus] + '10'}
                  variant="subtle"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontWeight="medium"
                  fontSize="10px"
                >
                  {READABLE_MEMBERSHIP_STATUS[displayMembershipStatus]}
                </Badge>
              )}

              {/* Switch on: hide ALL extra alerts — only Active Member / Non-Member.
                  Also wait for config load so alerts don't flash then disappear. */}
              {!displayConfigLoading &&
                !showActiveAndNonMembersOnly &&
                organization.membershipData?.mostRelevantMembershipTermAlert && (
                <MostRelevantMembershipTermAlertTag
                  mostRelevantMembershipTermAlert={
                    organization.membershipData.mostRelevantMembershipTermAlert
                  }
                />
              )}
            </HStack>
          </Td>
        ) : (
          <Td pr="2" pl="2">
            <Badge
              color={organization.isMember ? '#04AD04' : '#B87E00'}
              bg={
                organization.isMember
                  ? 'rgba(4, 173, 4, 0.1)'
                  : 'rgba(184, 126, 0, 0.1)'
              }
              variant="subtle"
              px={2}
              py={1}
              borderRadius="md"
              fontWeight="medium"
              fontSize="10px"
            >
              {organization.isMember ? 'Member' : 'Non-Member'}
            </Badge>
          </Td>
        )}

        {/* {memberManagement.isActive && (
          <Td pr="2" pl="2">
            <Tooltip
              label={
                organization.membershipData?.currentMembershipTerm?.membershipType
                  ?.name ?? 'None'
              }
            >
              <Text>{membershipTypeName}</Text>
            </Tooltip>
          </Td>
        )} */}

        {memberManagement.isActive && (
        <Td pr="2" pl="2">
          <Tooltip
            label={
              organization.membershipData?.currentMembershipTerm?.membershipType?.name ??
              'None'
            }
          >
            <Text>
              {truncate(
                organization.membershipData?.currentMembershipTerm?.membershipType
                  ?.name ?? 'None',
                { length: 25 }
              )}
            </Text>
          </Tooltip>
        </Td>
      )}

        <Td pr="2" pl="2">
          <Badge
            color={organization.hidden ? '#B87E00' : '#04AD04'}
            bg={
              organization.hidden
                ? 'rgba(184, 126, 0, 0.1)'
                : 'rgba(4, 173, 4, 0.1)'
            }
            variant="subtle"
            px={2}
            py={1}
            borderRadius="md"
            fontWeight="medium"
            fontSize="10px"
          >
            {organization.hidden
              ? archivedOnly
                ? 'Archived'
                : 'Hidden'
              : 'Active'}
          </Badge>
        </Td>

        <Td pr="2" pl="2">
          {organization.isSponsor ? 'Sponsor' : 'Record'}
        </Td>

        <Td pr="2" pl="2" w="80px" minW="80px" maxW="80px" boxShadow="-8px 0 12px -12px rgba(0,0,0,0.35)"  whiteSpace="nowrap" position="sticky" right={0} zIndex={2} bg="white" textAlign="left">
          <Menu placement="bottom-end" strategy="fixed">
            <MenuButton
              as={IconButton}
              aria-label="Organization actions"
              icon={<MoreHorizontalIcon transform="rotate(90deg)" />}
              size="sm"
              colorScheme="primary"
              variant="ghost"
              ml="3px"
              onClick={(e) => e.stopPropagation()}
            />
            <Portal>
            <MenuList zIndex="popover" onClick={(e) => e.stopPropagation()}>
              {archivedOnly ? (
                <MenuItem
                  fontSize="sm"
                  isDisabled={isRestoring}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRestoreOrganization();
                  }}
                >
                  <CheckCircleIcon boxSize={5} mr={2} color="green.500" />
                  Restore
                </MenuItem>
              ) : (
                <>
                  <MenuItem fontSize="sm" onClick={editModal.onOpen}><EditIcon boxSize={5} mr={2} color="primary.500" /> Edit</MenuItem>
                  <MenuItem fontSize="sm" onClick={handleView}><EyeIcon boxSize={5} mr={2} color="primary.500" /> View</MenuItem>
                </>
              )}
            </MenuList>
            </Portal>
          </Menu>
        </Td>
      </Tr>

      <Tr>
        <Td colSpan={colSpan} p="0" border="0">
          {isExpanded && (
          <Collapse in={isExpanded} animateOpacity>

           <Flex p={3} bg="secondary.500" direction="row" align="stretch"  minH="full" gap={3}  w="full">
                        {/* Contact Info Section */}
                        <Box bg="white" p="0" borderRadius="10px" w="50%" display="flex">
                          <VStack align="start" spacing={4} w="full" h="full">
                            
                             <Card w="full">
                              <Box position="relative">
                                <Image
                                  w="full"
                                  maxH="130px"
                                  borderTopRadius="xl"
                                  src={
                                    bannerImage?.imageProxy ||
                                    undefined
                                  }
                                  style={{objectFit: "cover"}}
                                  fallback={
                                    <Stack bg="#D9D9D9" height="130px" w="full" borderTopLeftRadius="10px" borderTopRightRadius="10px"></Stack>
                                  }
                                  alt="company banner"
                                />
          
                                <BannerImageMenu
                                  hasBannerImage={
                                    !!bannerImage?.imageProxy
                                  }
                                  allowRemove={true}
                                  updateBannerImage={getUpdate('banner')}
                                  isLoading={isUpdatingImage}
                                  iconView={true}
                                />
                              </Box>
          
                              <Stack direction="row" p="3">
                                <OrganizationProfileImage
                                  mt="-12"
                                  mr="2"
                                  organization={detailOrganization}
                                  flexShrink="0"
                                  // w="139px"
                                  // h="126px"
                                  w="100px"
                                  h="90px"
                                >
                                  <ProfileImageMenu
                                    hasProfileImage={
                                     !!detailOrganization.profilePicture?.imageProxy
                                    }
                                    updateProfileImage={getUpdate('profile')}
                                    isLoading={isUpdatingImage}
                                    objectFit="contain"
                                    iconView={true}
                                  />
                                </OrganizationProfileImage>
                                <Stack spacing="1" width="full">
                                    <Stack spacing={2} w="full" minW="100%">
                                      <Flex justify="space-between" align="stretch" w="full">
                                        <HStack spacing={2} align="center" w='100%'>
                                          <Heading size="sm" noOfLines={2} whiteSpace="normal"  fontFamily="Roboto, sans-serif">
                                            {detailOrganization.displayName}
                                          </Heading>
                                          {organization.membershipData?.membershipStatus && (
                                            <Badge
                                              color={
                                                MEMBERSHIP_STATUS_COLOR[membershipStatus]
                                              }
                                              bg={
                                                MEMBERSHIP_STATUS_COLOR[membershipStatus] + '10'
                                              }
                                              variant="subtle"
                                              px={2}
                                              py={1}
                                              borderRadius="md"
                                              fontWeight="medium"
                                              fontSize="10px"
                                              height="fit-content"
                                            >
                                              {
                                                READABLE_MEMBERSHIP_STATUS[membershipStatus]
                                              }
                                            </Badge>
                                          )}
                                        </HStack>
          
                                        <Box color="primary.500" cursor="pointer" >
                                          <Menu placement="bottom-end">
                                             <MenuButton  
                                                size="sm"
                                                as={IconButton}
                                                variant="outline"
                                                colorScheme="primary"
                                                bg="transparent"
                                                borderColor="transparent"
                                                ml="1"
                                                icon={<MoreVerticalIcon color="primary.500" />}>
                                                </MenuButton>
                                            <MenuList>
                                                <MenuItem onClick={editModal.onOpen} color="textSecondary.500"><EditIcon boxSize={5} mr={2} color="primary.500" /> Edit Info</MenuItem>
                                            </MenuList>
                                          </Menu>
                                        </Box>
                                      </Flex>
          
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={2} spacingY={2} >
                                        <HStack spacing={2} align="center" minW="50%">
                                          <Text color="primary.500" >
                                            <EmailIcon boxSize="18px" overflow="visible" mt="-8px" />
                                          </Text>
                                          <Text fontSize="sm" fontWeight="500" color="gray.700" wordBreak="break-word" whiteSpace="pre-wrap">
                                            {attributes.email || "-"}
                                          </Text>
                                        </HStack>

                                        <HStack spacing={2} align="center" minW="50%">
                                          <Text color="primary.500">
                                            <PhoneNoIcon boxSize="18px" overflow="visible" mt="-5px" />
                                          </Text>
                                          <Text fontSize="sm" fontWeight="500" color="gray.700" whiteSpace="nowrap">
                                              {attributes.phone || "-"}
                                          </Text>
                                        </HStack>

                                        <HStack spacing={2} align="center">
                                          <Text color="primary.500">
                                            <GlobeIcon boxSize="18px" overflow="visible" mt="-5px" />
                                          </Text>
                                          <Text fontSize="sm" fontWeight="500" color="gray.700" wordBreak="break-word" whiteSpace="pre-wrap">
                                          {attributes.website || "-"}
                                          </Text>
                                        </HStack>

                                        <HStack spacing={2} align="center">
                                          <Text color="primary.500">
                                            <MapPinIcon boxSize="18px" mt="-4px"/>
                                          </Text>
                                          <Text fontSize="sm" fontWeight="500" color="gray.700" whiteSpace="normal">
                                          {attributes.location || "-"}
                                          </Text>
                                        </HStack>
                                      </SimpleGrid>
                                    </Stack>
                                </Stack>
                              </Stack>
                            </Card>
                      
                      
                          </VStack>
                        </Box>
          
                        {/* Membership Lifecycle Section */}
                       <Box w="50%" display="flex">
                        {memberManagement.isActive && (
                          <Box w="50%" display="flex" h="full" flex="1">
                            {isLoadingDetail ? (
                              <Flex
                                bg="white"
                                p="4"
                                minH="184px"
                                borderRadius="10px"
                                align="center"
                                justify="center"
                                h="full"
                                flex="1"
                              >
                                <Text color="gray.500" fontSize="sm">
                                  Loading membership...
                                </Text>
                              </Flex>
                            ) : membershipData ? (
                              <AccordionMembershipCard
                                organizationId={result._id}
                                membershipData={membershipData as any}
                                displayName={result.displayName}
                                creditBalance={
                                  detailResult?.creditBalance
                                    ? dinero(detailResult.creditBalance)
                                    : dinero({ amount: 0, currency })
                                }
                                organizationIsOwnerless={
                                  !(detailResult?.accountOwners?.length ??
                                    result.accountOwners?.length)
                                }
                                shouldBeAdminView
                                variant="new"
                              />
                            ) : (
                              <Flex
                                bg="white"
                                p="4"
                                borderRadius="10px"
                                align="center"
                                justify="center"
                                h="full"
                                minH="full"
                                flex="1"
                              >
                                <Text color="gray.500" fontSize="sm">No membership data</Text>
                              </Flex>
                            )}
                          </Box>
                        )}

                         {!memberManagement.isActive &&  (
                            <MembershipEmptyCard hidden={organization.hidden} organizationView={true} 
                            isMember={organization.isMember} isSponsor={organization.isSponsor} />
                          )}
                       </Box>
                        
                      </Flex>
            
          </Collapse>)}
        </Td>
      </Tr>
      {editModal.isOpen && (
        <CompanyInfoEdit
          organization={organization as unknown as CompanyInfoEdit_OrganizationFragment}
          isOpen={editModal.isOpen}
          onClose={editModal.onClose}
        />
      )}
    </>
  );
};